import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, Info } from "lucide-react";

import { Switch } from "../../../components/ui/switch";
import { CarregandoLista } from "../../../components/carregando";

const API = "/api";

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

/**
 * Módulo Fiscal → aba FORMAS DE PAGAMENTO.
 *
 * Regra do dono (24/09/2026): quem decide se a venda gera nota é a FORMA DE
 * PAGAMENTO, em qualquer módulo. Switch ligado emite sempre; desligado, nunca.
 * É o MESMO campo (`emitir_nf_auto`) do switch em Financeiro → Formas de
 * Pagamento — mexeu aqui, muda lá.
 */
export default function FormasPagamentoFiscal() {
  const [formas, setFormas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [salvandoId, setSalvandoId] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payment-methods`, getAuthHeader());
      const lista = Array.isArray(res.data) ? res.data : res.data?.payment_methods || [];
      setFormas(lista.filter((f) => f.is_active));
    } catch {
      toast.error("Não foi possível carregar as formas de pagamento");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const alternar = async (forma, ligado) => {
    setSalvandoId(forma.id);
    setFormas((prev) => prev.map((f) => (f.id === forma.id ? { ...f, emitir_nf_auto: ligado } : f)));
    try {
      await axios.put(`${API}/payment-methods/${forma.id}`, { emitir_nf_auto: ligado }, getAuthHeader());
      toast.success(ligado ? `${forma.name}: emite nota` : `${forma.name}: não emite nota`);
    } catch {
      setFormas((prev) => prev.map((f) => (f.id === forma.id ? { ...f, emitir_nf_auto: !ligado } : f)));
      toast.error("Não foi possível salvar");
    } finally {
      setSalvandoId(null);
    }
  };

  if (carregando) {
    // Carregamento com cards (25/10/2026): as linhas das formas piscando
    return <CarregandoLista linhas={5} avatar={false} colunas={1} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/40 border border-border/60 text-sm">
        <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div className="space-y-1 text-muted-foreground">
          <p>
            <span className="font-medium text-foreground">Ligado, emite sempre</span> — em qualquer
            módulo. Desligado, nunca emite.
          </p>
          <p>
            {/* Mesmo momento de backend/api/v1/fiscal/regras.py (momento_de_emitir) — achado 360 */}
            Entrega emite quando entra em produção; Balcão e Totem, na venda; retirada, na
            conclusão; mesa, uma nota por conta no fechamento. Pagamento dividido só emite se todas
            as formas estiverem ligadas. Pedidos do iFood não emitem.
          </p>
        </div>
      </div>

      {formas.length === 0 ? (
        <p className="text-sm text-muted-foreground p-6 text-center">
          Nenhuma forma de pagamento ativa. Cadastre em Financeiro → Formas de Pagamento.
        </p>
      ) : (
        <div className="rounded-xl border border-border/60 divide-y divide-border/60 bg-card">
          {formas.map((f) => (
            <label
              key={f.id}
              className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer"
            >
              <span className="flex items-center gap-2 text-sm font-medium min-w-0">
                <CreditCard className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="truncate">{f.name}</span>
              </span>
              <span className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-muted-foreground">
                  {f.emitir_nf_auto ? "Emite nota" : "Não emite"}
                </span>
                <Switch
                  checked={Boolean(f.emitir_nf_auto)}
                  disabled={salvandoId === f.id}
                  onCheckedChange={(v) => alternar(f, v)}
                  aria-label={`Emitir nota para ${f.name}`}
                />
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
